import { CaseStatus } from 'dashboard/models/case';
import {
  CaseAnalyticsAssignmentDistribution,
  CaseAnalyticsIntervals,
  CaseAnalyticsResolutionDistribution,
  CaseAnalyticsResolutionsByTag,
  CaseAnalyticsResolutionTimes,
  CaseAnalyticsStatusDistributionCurrentUser,
  CaseAnalyticsTagDistribution,
} from 'dashboard/models/case-analytics';
import { CaseTemplate } from 'dashboard/models/case-template';
import Action from 'lib/interfaces/Action';
import { Iso8601DateString } from 'lib/types';

export enum CaseAnalyticsFilterDateType {
  CreatedAt = 'created_at',
  ResolvedAt = 'resolved_at',
}

export interface CaseAnalyticsFilterParams {
  startDate: Iso8601DateString;
  endDate: Iso8601DateString;
  dateType: CaseAnalyticsFilterDateType;
  caseStatus: CaseStatus[];
  caseTagIds: string[];
  caseTemplateIds: string[];
  caseStateIds: string[];
  assigneeIds: string[];
  teamAssigneeIds: string[];
  resolverIds: string[];
  teamResolverIds: string[];
}

export type CaseAnalyticsActions =
  | Action<CaseAnalyticsActionType.SetCaseAnalyticsIntervals, CaseAnalyticsIntervalsPayload>
  | Action<
      CaseAnalyticsActionType.SetCaseAnalyticsResolutionTimes,
      CaseAnalyticsResolutionTimesPayload
    >
  | Action<
      CaseAnalyticsActionType.SetCaseAnalyticsAssignmentDistribution,
      CaseAnalyticsAssignmentDistributionPayload
    >
  | Action<
      CaseAnalyticsActionType.SetCaseAnalyticsResolutionDistribution,
      CaseAnalyticsResolutionDistributionPayload
    >
  | Action<
      CaseAnalyticsActionType.SetCaseAnalyticsTagDistribution,
      CaseAnalyticsTagDistributionPayload
    >
  | Action<
      CaseAnalyticsActionType.SetCaseAnalyticsResolutionsByTag,
      CaseAnalyticsResolutionsByTagPayload
    >
  | Action<
      CaseAnalyticsActionType.SetCaseAnalyticsStatusDistributionForCurrentUser,
      CaseAnalyticsStatusDistributionForCurrentUserPayload
    >
  | Action<CaseAnalyticsActionType.SetCaseTemplates, CaseTemplatesPayload>
  | Action<CaseAnalyticsActionType.SetCaseAnalyticsFilters, CaseAnalyticsFiltersPayload>
  | Action<CaseAnalyticsActionType.ResetCaseAnalyticsDateFilter>
  | Action<CaseAnalyticsActionType.ClearCaseAnalytics>;

export enum CaseAnalyticsActionType {
  SetCaseAnalyticsFilters = 'case-analytics/SET_CASE_ANALYTICS_FILTERS',
  SetCaseAnalyticsIntervals = 'case-analytics/SET_CASE_ANALYTICS_INTERVALS',
  SetCaseAnalyticsResolutionTimes = 'case-analytics/SET_CASE_ANALYTICS_RESOLUTION_TIMES',
  SetCaseAnalyticsAssignmentDistribution = 'case-analytics/SET_CASE_ANALYTICS_ASSIGNMENT_DISTRIBUTION',
  SetCaseAnalyticsResolutionDistribution = 'case-analytics/SET_CASE_ANALYTICS_RESOLUTION_DISTRIBUTION',
  SetCaseAnalyticsTagDistribution = 'case-analytics/SET_CASE_ANALYTICS_TAG_DISTRIBUTION',
  SetCaseAnalyticsResolutionsByTag = 'case-analytics/SET_CASE_ANALYTICS_RESOLUTIONS_BY_TAG',
  SetCaseAnalyticsStatusDistributionForCurrentUser = 'case-analytics/SET_CASE_ANALYTICS_STATUS_DISTRIBUTION_FOR_CURRENT_USER',
  SetCaseTemplates = 'case-analytics/SET_CASE_TEMPLATES',
  ResetCaseAnalyticsDateFilter = 'case-analytics/RESET_CASE_ANALYTICS_DATE_FILTERS',
  ClearCaseAnalytics = 'case-analytics/CLEAR_CASE_ANALYTICS',
}

interface CaseAnalyticsIntervalsPayload {
  caseStatusOverTime: CaseAnalyticsIntervals;
  queriedAt: Iso8601DateString;
  updatedAt: Iso8601DateString;
}

export function setCaseAnalyticsIntervals(
  caseStatusOverTime: CaseAnalyticsIntervals,
  queriedAt: Iso8601DateString,
  updatedAt: Iso8601DateString,
): Action<CaseAnalyticsActionType.SetCaseAnalyticsIntervals, CaseAnalyticsIntervalsPayload> {
  return {
    payload: {
      caseStatusOverTime,
      queriedAt,
      updatedAt,
    },
    type: CaseAnalyticsActionType.SetCaseAnalyticsIntervals,
  };
}

interface CaseAnalyticsResolutionTimesPayload {
  caseResolutionTimes: CaseAnalyticsResolutionTimes;
  queriedAt: Iso8601DateString;
}

export function setCaseAnalyticsResolutionTimes(
  caseResolutionTimes: CaseAnalyticsResolutionTimes,
  queriedAt: Iso8601DateString,
): Action<
  CaseAnalyticsActionType.SetCaseAnalyticsResolutionTimes,
  CaseAnalyticsResolutionTimesPayload
> {
  return {
    payload: {
      caseResolutionTimes,
      queriedAt,
    },
    type: CaseAnalyticsActionType.SetCaseAnalyticsResolutionTimes,
  };
}

interface CaseAnalyticsAssignmentDistributionPayload {
  caseAssignmentDistribution: CaseAnalyticsAssignmentDistribution;
  queriedAt: Iso8601DateString;
}

export function setCaseAnalyticsAssignmentDistribution(
  caseAssignmentDistribution: CaseAnalyticsAssignmentDistribution,
  queriedAt: Iso8601DateString,
): Action<
  CaseAnalyticsActionType.SetCaseAnalyticsAssignmentDistribution,
  CaseAnalyticsAssignmentDistributionPayload
> {
  return {
    payload: {
      caseAssignmentDistribution,
      queriedAt,
    },
    type: CaseAnalyticsActionType.SetCaseAnalyticsAssignmentDistribution,
  };
}

interface CaseAnalyticsResolutionDistributionPayload {
  caseResolutionDistribution: CaseAnalyticsResolutionDistribution;
  queriedAt: Iso8601DateString;
}

export function setCaseAnalyticsResolutionDistribution(
  caseResolutionDistribution: CaseAnalyticsResolutionDistribution,
  queriedAt: Iso8601DateString,
): Action<
  CaseAnalyticsActionType.SetCaseAnalyticsResolutionDistribution,
  CaseAnalyticsResolutionDistributionPayload
> {
  return {
    payload: {
      caseResolutionDistribution,
      queriedAt,
    },
    type: CaseAnalyticsActionType.SetCaseAnalyticsResolutionDistribution,
  };
}

interface CaseAnalyticsTagDistributionPayload {
  caseTagDistribution: CaseAnalyticsTagDistribution;
  queriedAt: Iso8601DateString;
}

export function setCaseAnalyticsTagDistribution(
  caseTagDistribution: CaseAnalyticsTagDistribution,
  queriedAt: Iso8601DateString,
): Action<
  CaseAnalyticsActionType.SetCaseAnalyticsTagDistribution,
  CaseAnalyticsTagDistributionPayload
> {
  return {
    payload: {
      caseTagDistribution,
      queriedAt,
    },
    type: CaseAnalyticsActionType.SetCaseAnalyticsTagDistribution,
  };
}

interface CaseAnalyticsResolutionsByTagPayload {
  caseResolutionsByTag: CaseAnalyticsResolutionsByTag;
  queriedAt: Iso8601DateString;
}

export function setCaseAnalyticsResolutionsByTag(
  caseResolutionsByTag: CaseAnalyticsResolutionsByTag,
  queriedAt: Iso8601DateString,
): Action<
  CaseAnalyticsActionType.SetCaseAnalyticsResolutionsByTag,
  CaseAnalyticsResolutionsByTagPayload
> {
  return {
    payload: {
      caseResolutionsByTag,
      queriedAt,
    },
    type: CaseAnalyticsActionType.SetCaseAnalyticsResolutionsByTag,
  };
}

interface CaseAnalyticsStatusDistributionForCurrentUserPayload {
  caseStatusDistributionForCurrentUser: CaseAnalyticsStatusDistributionCurrentUser;
}

export function setCaseAnalyticsStatusDistributionForCurrentUser(
  caseStatusDistributionForCurrentUser: CaseAnalyticsStatusDistributionCurrentUser,
) {
  return {
    payload: {
      caseStatusDistributionForCurrentUser,
    },
    type: CaseAnalyticsActionType.SetCaseAnalyticsStatusDistributionForCurrentUser,
  };
}

interface CaseTemplatesPayload {
  caseTemplates: CaseTemplate[];
}

export function setCaseTemplates(
  caseTemplates: CaseTemplate[],
): Action<CaseAnalyticsActionType.SetCaseTemplates, CaseTemplatesPayload> {
  return {
    payload: {
      caseTemplates,
    },
    type: CaseAnalyticsActionType.SetCaseTemplates,
  };
}

export function clearCaseAnalytics(): Action<CaseAnalyticsActionType.ClearCaseAnalytics> {
  return {
    type: CaseAnalyticsActionType.ClearCaseAnalytics,
  };
}

interface CaseAnalyticsFiltersPayload {
  filters: Partial<CaseAnalyticsFilterParams>;
}

export function setCaseAnalyticsFilters(
  filters: Partial<CaseAnalyticsFilterParams>,
): Action<CaseAnalyticsActionType.SetCaseAnalyticsFilters, CaseAnalyticsFiltersPayload> {
  return {
    payload: {
      filters,
    },
    type: CaseAnalyticsActionType.SetCaseAnalyticsFilters,
  };
}

export function resetCaseAnalyticsDateFilter(): Action<CaseAnalyticsActionType.ResetCaseAnalyticsDateFilter> {
  return {
    type: CaseAnalyticsActionType.ResetCaseAnalyticsDateFilter,
  };
}
